import React from 'react';
import { Box, Typography, Button, styled, useTheme, useMediaQuery } from '@mui/material';
import { Link } from 'gatsby';
import HomeLayout from '../components/layouts/HomeLayout';
import Feedback from '../components/Feedback';
import VideoSection from '../components/VideoSection';
import HowItWorksSection from '../components/HowItWorksSection';
import PricingSection from '../components/PricingSection';
import CTASection from '../components/CTASection';
import FAQSection from '../components/FAQSection';
import Hero from '../components/Hero';
import ContactSection from '../components/ContactSection';
import FeatureSection from '../components/FeatureSection';

const MainTitle = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  color: 'white',
  fontSize: '80px',
  fontFamily: 'Raleway, sans-serif',
  fontWeight: 700,
  lineHeight: '85px',
  letterSpacing: '52.80px',
  wordWrap: 'break-word',
  [theme.breakpoints.down('md')]: {
    fontSize: '60px',
    lineHeight: '65px',
    letterSpacing: '35px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '40px',
    lineHeight: '45px',
    letterSpacing: '20px',
  },
  [theme.breakpoints.down('xs')]: {
    fontSize: '32px',
    lineHeight: '38px',
    letterSpacing: '15px',
  },
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  color: 'white',
  fontSize: '32px',
  fontFamily: 'Raleway, sans-serif',
  fontWeight: 400,
  lineHeight: '75px',
  letterSpacing: '7.04px',
  wordWrap: 'break-word',
  [theme.breakpoints.down('md')]: {
    fontSize: '28px',
    lineHeight: '60px',
    letterSpacing: '6px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '22px',
    lineHeight: '45px',
    letterSpacing: '4.5px',
  },
  [theme.breakpoints.down('xs')]: {
    fontSize: '18px',
    lineHeight: '38px',
    letterSpacing: '3.5px',
  },
}));

const ActionButton = styled(Button)(({ theme, variant }) => ({
  padding: '15px 25px',
  borderRadius: '35px',
  backdropFilter: 'blur(5.85px)',
  fontSize: '16px',
  fontFamily: 'Raleway, sans-serif',
  fontWeight: 500,
  ...(variant === 'contained' ? {
    background: 'linear-gradient(130deg, rgba(251.85, 243.15, 226.99, 0.70) 0%, #ECA914 100%)',
    color: '#010101',
  } : {
    border: '1px solid white',
    color: '#E8C694',
  }),
  [theme.breakpoints.down('sm')]: {
    padding: '12px 20px',
    fontSize: '14px',
  },
}));

const serviceCards = [
  "Software Development",
  "Agile Project Management",
  "Creative Solutions",
  "Strategic Consulting",
  "UX/UI Design",
  "Startup Tech",
  "Salesforce Consulting",
  "Web3\nDevelopment",
  "Manufacturing Solutions",
  "Artificial Intelligence",
  "DevOps Consulting",
  "Recruitment Services"
];

const IndexPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const handleContactClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <HomeLayout>
      
      {/* Hero Section */}
      <Box
        component="section"
        id="hero"
        sx={{
          minHeight: '100vh',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          color: 'white',
          position: 'relative',
          background: '#050505',
        }}
      >
        <Hero />
        <Box 
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 20,
            width: '100%',
            padding: { xs: '0 1rem', sm: '0 2rem', md: '0' },
            textShadow: { xs: '0 1px 3px rgba(0,0,0,0.7)', sm: '0 1px 2px rgba(0,0,0,0.5)', md: 'none' },
            backdropFilter: { xs: 'blur(1px)', sm: 'none' },
            backgroundColor: { xs: 'rgba(5,5,5,0.3)', sm: 'transparent' },
            borderRadius: '8px',
          }}
        >
          <MainTitle variant="h1">
            THE<br/>NEXT LEVEL
          </MainTitle>
          <Subtitle variant="h4" sx={{ 
            mt: { xs: 1, sm: 1.5, md: 2 }, 
            mb: { xs: 2, sm: 3, md: 4 } 
          }}>
            YOUR REMOTE TEAM BUT CLOSER
          </Subtitle>
          <Box sx={{ 
            display: 'flex', 
            gap: { xs: 1, sm: 1.5, md: 2 }, 
            justifyContent: 'center', 
            mt: { xs: 2, sm: 3, md: 4 },
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'center',
          }}>
            <ActionButton variant="contained" onClick={handleContactClick}>
              Contact Us
            </ActionButton>
            <Link to="/services" style={{ textDecoration: 'none', width: isMobile ? '100%' : 'auto' }}>
              <ActionButton variant="outlined" fullWidth={isMobile}>
                See All Services
              </ActionButton>
            </Link>
          </Box>
        </Box>
      </Box>

      <Box sx={{ 
        width: '100%', 
        background: '#050505',
        overflow: 'hidden'
      }}>
        {/* Feature Section */}
        <FeatureSection />

        {/* Other Sections */}
        <HowItWorksSection />
        <PricingSection />
        <CTASection />
        <FAQSection />
        <Box component="section" id="contact">
          <ContactSection />
        </Box>
      </Box>
    </HomeLayout>
  );
};

export default IndexPage; 